import React, { useEffect, useState } from 'react';
import * as motion from 'motion/react-m';
import { Box, Flex } from '@osmosis/react-ui/components/Box';
import { Icon } from '@osmosis/react-ui/components/Icon';
import { useQuizContext } from '../../../../context/QuizContext';
import {
  OrderedResponseData,
  OrderedResponseSubmittedAnswerType,
} from '../../../../types/quiz';
import { P } from '@osmosis/react-ui/components/Text';
import QuizSubmitButtons from '../../QuizSubmitButtons';
import OrderedResponseAnswersGrid from './OrderedResponseAnswersGrid';
import {
  CorrectBanner,
  ExplanationDropDown,
  ExplanationMessage,
} from '../utils';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { slideVertical } from '../../../../styles/animation';

const AnswerListText = ({
  text,
  explanation,
  isCorrect,
}: {
  text: string;
  explanation: string;
  isCorrect: boolean;
}) => {
  const { isAnswered } = useQuizContext();
  const [isExplanation, setExplanation] = useState(false);

  return (
    <Box as="li" width="70%" sx={{ textAlign: 'left' }} flex={1}>
      {isAnswered && isCorrect && (
        <Box sx={{ position: 'absolute', left: '15px' }}>
          <Icon icon={faCheck} fontSize="20px" color="green" />
        </Box>
      )}

      <P>{text}</P>

      {isAnswered && !isExplanation && explanation && (
        <ExplanationDropDown
          setExplanationVisible={setExplanation}
          isOpen={isExplanation}
        />
      )}

      {isExplanation && explanation && (
        <ExplanationMessage explanation={explanation} />
      )}
    </Box>
  );
};

/**
 * Answers list for ordered response.
 */
const OrderedResponseAnswersList = () => {
  const { currentItem, submitAnswer, isAnswered, currentResponseItem } =
    useQuizContext();
  const currentData = currentItem?.data as OrderedResponseData;
  const [checkedOptions, setCheckedOptions] =
    useState<OrderedResponseSubmittedAnswerType>({});
  const isChosen =
    Object.keys(checkedOptions).length === currentData.answers.length;

  const correctOptions: OrderedResponseSubmittedAnswerType =
    currentData.answers.reduce(
      (acc, cur, index) => ({ ...acc, [index]: cur.correctValue }),
      {},
    );

  const isCorrect =
    JSON.stringify(checkedOptions) === JSON.stringify(correctOptions);

  useEffect(() => {
    setCheckedOptions({});
  }, [currentItem?.nodeID]);

  return (
    <Flex flexDirection="column" width="100%">
      <Box as="ol">
        {currentData.answers.map(({ explanation, text }, index) => (
          <Flex
            key={`${currentItem?.nodeID}-${text}`}
            justifyContent="space-between"
            my="10px"
            alignItems="flex-start"
          >
            <AnswerListText
              explanation={explanation}
              text={text}
              isCorrect={correctOptions[index] === checkedOptions[index]}
            />

            <OrderedResponseAnswersGrid
              row={index}
              checkedOptions={
                (currentResponseItem?.response
                  ?.answer as OrderedResponseSubmittedAnswerType) ||
                checkedOptions
              }
              correctOptions={correctOptions}
              setCheckedOptions={setCheckedOptions}
            />
          </Flex>
        ))}
      </Box>
      {isChosen && !isAnswered && (
        <motion.div {...slideVertical()}>
          <QuizSubmitButtons
            submit={(c) => submitAnswer(c, isCorrect, checkedOptions)}
          />
        </motion.div>
      )}

      {isAnswered && <CorrectBanner isCorrectAnswer={isCorrect} />}
    </Flex>
  );
};

export default OrderedResponseAnswersList;
